import { Link, NavLink, useLocation } from "react-router-dom";
import ThemeToggle from "@/components/ThemeToggle";
import { useState, useEffect } from "react";
import { Menu, X, Instagram, Linkedin, Youtube, Github } from "lucide-react";

const SITE_TITLE = "Marcus Cylar | Ministry. Cybersecurity.";
const PAGE_TITLES = {
  "/": SITE_TITLE,
  "/about": "About | Marcus Cylar",
  "/services": "Services | Marcus Cylar",
  "/speaking": "Speaking | Marcus Cylar",
  "/speaking/book": "Book Marcus | Speaking",
  "/insights": "Insights | Marcus Cylar",
  "/contact": "Contact | Marcus Cylar",
  "/admin": "Admin · Marcus Cylar",
  "/admin/login": "Admin Login · Marcus Cylar",
};

const SOCIAL = [
  { href: "https://instagram.com/marcuscylar", label: "Instagram", icon: Instagram, handle: "@marcuscylar" },
  { href: "https://linkedin.com/in/marcusacylar", label: "LinkedIn", icon: Linkedin, handle: "@marcusacylar" },
  { href: "https://youtube.com/@MarcusACylar", label: "YouTube", icon: Youtube, handle: "@MarcusACylar" },
  { href: "https://github.com/marcusacylar", label: "GitHub", icon: Github, handle: "@marcusacylar" },
];

const NAV = [
  { to: "/", label: "Home", id: "01" },
  { to: "/about", label: "About", id: "02" },
  { to: "/services", label: "Services", id: "03" },
  { to: "/speaking", label: "Speaking", id: "04" },
  { to: "/insights", label: "Insights", id: "05" },
  { to: "/contact", label: "Contact", id: "06" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    let title = PAGE_TITLES[loc.pathname];
    if (!title) {
      if (loc.pathname.startsWith("/insights/")) title = "Insight | Marcus Cylar";
      else title = SITE_TITLE;
    }
    document.title = title;
  }, [loc.pathname]);

  return (
    <div className="relative min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="font-serif text-xl tracking-tight">Marcus Cylar</span>
            <span className="hidden sm:inline font-mono text-[10px] text-muted-foreground uppercase tracking-[0.25em]">
              [ Ministry · Cybersecurity ]
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `text-sm link-underline transition-colors ${
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <ThemeToggle />
          </nav>
          <div className="lg:hidden flex items-center gap-3">
            <ThemeToggle />
            <button onClick={() => setOpen((v) => !v)} data-testid="nav-menu-btn" aria-label="Menu" className="p-2 rounded-md border border-border">
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
            <div className="px-6 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between py-2 text-sm"
                  end={n.to === "/"}
                >
                  <span>{n.label}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">[ {n.id} ]</span>
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 mt-24 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid gap-8 md:grid-cols-3">
          <div>
            <div className="font-serif text-2xl tracking-tight">Marcus Cylar</div>
            <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-[0.2em] mt-2">
              Ministry. Cybersecurity.
            </div>
            <p className="text-sm text-muted-foreground mt-4 max-w-xs">
              Your human in the loop. Helping organizations reduce cyber risk and increase resilience.
            </p>
          </div>
          <div className="md:justify-self-center">
            <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-[0.2em] mb-3">Site</div>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="link-underline text-muted-foreground hover:text-foreground">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:justify-self-end">
            <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-[0.2em] mb-3">Elsewhere</div>
            <ul className="space-y-2.5 text-sm">
              {SOCIAL.map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.label}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`footer-social-${s.label.toLowerCase()}`}
                      className="group inline-flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon className="w-4 h-4 text-accent" strokeWidth={1.75} />
                      <span className="link-underline">{s.label}</span>
                      <span className="font-mono text-[10px] text-muted-foreground/70">{s.handle}</span>
                    </a>
                  </li>
                );
              })}
              <li className="pt-1">
                <Link to="/admin" className="link-underline text-muted-foreground text-xs">Admin</Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between text-[11px] font-mono text-muted-foreground uppercase tracking-[0.2em]">
            <span>© {new Date().getFullYear()} Marcus Cylar</span>
            <span>{loc.pathname === "/" ? "[ home ]" : `[ ${loc.pathname.replace("/", "") || "home"} ]`}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
